/**
 * signalSeries — pure transforms for the indicator-series payload.
 *
 * The backend decides what each series *is* (which pane, line vs band vs
 * marker, dict sub-keys). This module decides how the payload is laid out for
 * recharts: one shared row array, panes grouped by scale, and colours assigned
 * in fixed order.
 *
 * Nothing here switches on attribute class names — add an indicator on the
 * backend and it appears here with no frontend change.
 */

// Categorical slots, assigned in fixed order and never cycled — colour follows
// the indicator, not its position in the response, so toggling one series off
// never repaints the others. Same convention as WfConfigCompare.
const SERIES_SLOTS = [
  'var(--series-1)', 'var(--series-2)', 'var(--series-3)',
  'var(--series-4)', 'var(--series-5)', 'var(--series-6)',
];

// Roles that get a fixed colour rather than a categorical slot, because they
// mean something specific rather than merely being "another series".
const ROLE_COLORS = {
  price: 'hsl(var(--foreground))',
  band: 'hsl(var(--muted-foreground))',
  ribbon_short: 'hsl(var(--win))',
  ribbon_long: 'hsl(var(--loss))',
  volume: 'hsl(var(--muted-foreground))',
  event: 'hsl(var(--primary))',
  state: 'hsl(var(--primary))',
};

export const PANE_PRICE = 'price';
export const PANE_VOLUME = 'volume';
export const PANE_BOUNDED = 'osc_0_100';
export const PANE_UNBOUNDED = 'osc_unbounded';
export const PANE_EVENT = 'event';
export const PANE_STATE = 'state';

const VALUE_PANES = [PANE_PRICE, PANE_VOLUME, PANE_BOUNDED, PANE_UNBOUNDED];

/** Series that are drawn as markers/chips rather than on a value axis. */
export const isAnnotation = (series) =>
  series.pane === PANE_EVENT || series.pane === PANE_STATE;

/**
 * Dependency series share one muted colour rather than taking slots.
 *
 * A GMMA ribbon is twelve EMAs but one idea, and the twelve are never read
 * individually — they are read as a band. Composite encoding (one hue, thin
 * and translucent, as a group) is what keeps them legible and keeps the
 * categorical palette from being cycled past its validated slots.
 */
const DEPENDENCY_COLOR = 'hsl(var(--muted-foreground))';

/** Every series belonging to one attribute instance shares an entity key. */
const entityKeyOf = (series) =>
  `${series.attr}:${JSON.stringify(series.params || {})}`;

/**
 * What colour follows.
 *
 * The three tracks of a Bollinger band are one indicator and share a hue,
 * told apart by dash — giving them three colours would read as three
 * unrelated lines. But a dict attribute's plain line keys are separate
 * measures (GMMATrend's five spread percentages, a Stochastic's %K and %D)
 * and do need telling apart, so they key separately.
 */
const colorKeyOf = (series) =>
  series.kind?.startsWith('band')
    ? entityKeyOf(series)
    : `${entityKeyOf(series)}#${series.key || ''}`;

/**
 * Assign a colour and a dash pattern to each series, pane by pane.
 *
 * Slots are assigned **within a pane**, not across the whole chart. Panes are
 * already a facet, so identity is carried by position plus hue, and no pane
 * here needs more than a couple of slots — which is what keeps the palette
 * from being cycled past its six validated steps. A pane that ever needed
 * more than six would be a signal to fold series together, not to invent a
 * seventh colour.
 *
 * Dash is secondary encoding on the band tracks: the palette's worst adjacent
 * pair validates at ΔE 8-9, which is only legal alongside something that is
 * not colour.
 */
export function decorate(seriesList = [], panes = null) {
  const paneOf = new Map();
  if (panes) {
    for (const pane of panes) {
      for (const series of pane.series) paneOf.set(series.id, pane.id);
    }
  }

  const slotCursor = new Map();
  const slotOf = new Map();

  for (const series of seriesList) {
    if (ROLE_COLORS[series.color_role] || series.declared === false) continue;
    const paneId = paneOf.get(series.id) ?? series.pane;
    const key = `${paneId}|${colorKeyOf(series)}`;
    if (slotOf.has(key)) continue;
    const next = slotCursor.get(paneId) ?? 0;
    slotOf.set(key, next);
    slotCursor.set(paneId, next + 1);
  }

  return seriesList.map((series) => {
    const paneId = paneOf.get(series.id) ?? series.pane;
    const key = `${paneId}|${colorKeyOf(series)}`;
    let color = ROLE_COLORS[series.color_role];
    if (series.declared === false) {
      color = DEPENDENCY_COLOR;
    } else if (!color) {
      color = SERIES_SLOTS[(slotOf.get(key) ?? 0) % SERIES_SLOTS.length];
    }
    return {
      ...series,
      entity: entityKeyOf(series),
      color,
      dash: dashFor(series),
    };
  });
}

function dashFor(series) {
  switch (series.kind) {
    case 'band_upper':
    case 'band_lower':
      return '4 3';
    case 'band_mid':
      return '2 2';
    default:
      return undefined;
  }
}

/**
 * Group value series into panes.
 *
 * One pane means one y-axis. A dual-axis pane is never an option: the
 * alignment of two y-scales is arbitrary, so it would invent a relationship
 * that is not in the data. Series of different scale get their own pane.
 *
 * Oscillators are therefore grouped by **attribute class**, because the class
 * is what fixes the unit. All EMASlope instances are percent-change of a
 * moving average and belong together; EMASlope and RSISlope are percent and
 * RSI-points and do not. Grouping by class also keeps a pane's guide lines
 * meaningful — merging RSI's 30/50/70 with Stochastic's 20/80 would put five
 * hairlines on one pane, none of which apply to both series.
 */
export function buildPanes(seriesList = [], { withPrice = false } = {}) {
  const panes = [];
  const byId = new Map();

  const push = (id, pane) => {
    if (!byId.has(id)) {
      byId.set(id, { id, ...pane, series: [] });
      panes.push(byId.get(id));
    }
    return byId.get(id);
  };

  // The close line is drawn from the bar itself, not from a series, so the
  // price pane must exist whenever there are bars — including the fallback
  // case where the replay failed and there are no indicator series at all.
  if (withPrice) {
    push(PANE_PRICE, { kind: PANE_PRICE, title: 'Price', height: 300 });
  }

  for (const series of seriesList) {
    if (!VALUE_PANES.includes(series.pane)) continue;

    if (series.pane === PANE_PRICE) {
      push(PANE_PRICE, { kind: PANE_PRICE, title: 'Price', height: 300 })
        .series.push(series);
    } else if (series.pane === PANE_VOLUME) {
      push(PANE_VOLUME, {
        kind: PANE_VOLUME,
        title: 'Volume',
        height: 84,
        // Bars are read by area, so the axis has to start at zero. On an auto
        // domain recharts floors the axis at the data minimum, which clips
        // every ordinary bar out of sight and leaves only the spikes showing.
        domain: [0, 'auto'],
      }).series.push(series);
    } else {
      const bounded = series.pane === PANE_BOUNDED;
      // Class, plus the domain for bounded ones so a 0-100 oscillator never
      // shares an axis with a 0-1 one even if they were the same class.
      const id = bounded
        ? `bounded:${series.attr}:${(series.domain || []).join('-')}`
        : `unbounded:${series.attr}`;
      const pane = push(id, {
        kind: bounded ? PANE_BOUNDED : PANE_UNBOUNDED,
        title: '',
        domain: bounded ? series.domain || [0, 100] : ['auto', 'auto'],
        guides: [],
        height: bounded ? 128 : 112,
      });
      pane.guides = Array.from(
        new Set([...pane.guides, ...(series.guides || [])])
      ).sort((a, b) => a - b);
      pane.series.push(series);
    }
  }

  // Title each oscillator pane after what it holds.
  for (const pane of panes) {
    if (pane.title) continue;
    const labels = Array.from(new Set(pane.series.map((s) => baseLabel(s))));
    pane.title = labels.join(' · ');
  }

  // Price first, then volume, then bounded oscillators ahead of unbounded
  // ones: RSI and Stochastic are what a reader reaches for, slopes are
  // supporting detail.
  const rank = {
    [PANE_PRICE]: 0,
    [PANE_VOLUME]: 1,
    [PANE_BOUNDED]: 2,
    [PANE_UNBOUNDED]: 3,
  };
  return panes
    .filter((pane) => pane.series.length > 0 || pane.id === PANE_PRICE)
    .sort((a, b) => rank[a.kind] - rank[b.kind]);
}

/** `RSI(14) %K` -> `RSI(14)` — the attribute label without the sub-key. */
function baseLabel(series) {
  if (!series.key) return series.label;
  const suffix = ` ${series.key}`;
  return series.label.endsWith(suffix)
    ? series.label.slice(0, -suffix.length)
    : series.label.replace(/\s+\S+$/, '');
}

/**
 * Zip `ohlcv` and every series into the single row array every pane renders
 * from. One shared array is what lets recharts keep the crosshair in step
 * across panes via `syncId`.
 */
export function buildRows(payload, seriesList = []) {
  const { ohlcv = [] } = payload || {};
  const rows = ohlcv.map((bar, i) => {
    const row = { ...bar, index: i };
    for (const series of seriesList) {
      row[series.id] = series.values?.[i] ?? null;
    }
    return row;
  });

  // Range tuples for banded indicators, so the span between the outer bands
  // can be filled rather than left as two unrelated lines.
  for (const series of seriesList) {
    if (series.kind !== 'band_upper' || !series.band_group) continue;
    const lower = seriesList.find(
      (s) => s.band_group === series.band_group && s.kind === 'band_lower'
    );
    if (!lower) continue;
    rows.forEach((row) => {
      const hi = row[series.id];
      const lo = row[lower.id];
      row[`${series.band_group}__range`] =
        hi == null || lo == null ? null : [lo, hi];
    });
  }

  return rows;
}

/**
 * Everything a chart needs from one payload.
 *
 * Panes are grouped first (grouping depends only on the backend's pane/attr
 * fields), then colours are assigned per pane, then the panes are regrouped so
 * each carries its decorated series. Grouping is deterministic, so the second
 * pass produces the same panes.
 */
export function prepareChart(payload) {
  const raw = payload?.series || [];
  const withPrice = (payload?.ohlcv || []).length > 0;
  const decorated = decorate(raw, buildPanes(raw, { withPrice }));
  const panes = buildPanes(decorated, { withPrice });
  return {
    series: decorated,
    panes,
    rows: buildRows(payload, decorated),
    visible: defaultVisible(decorated, panes),
  };
}

/** `{ 'YYYY-MM-DD': evaluation }` for O(1) lookup on hover. */
export function indexEvaluations(evaluations = []) {
  const out = {};
  for (const row of evaluations) out[row.date] = row;
  return out;
}

/**
 * Which series start visible.
 *
 * Declared price-pane series and the first two oscillator panes are on;
 * dependency series are off, or a GMMA screener would open with 24 lines.
 */
export function defaultVisible(seriesList = [], panes = []) {
  const visible = {};
  const oscPanes = panes
    .filter((p) => p.kind === PANE_BOUNDED || p.kind === PANE_UNBOUNDED)
    .slice(0, 2)
    .map((p) => p.id);

  for (const pane of panes) {
    const paneOn =
      pane.kind === PANE_PRICE ||
      pane.kind === PANE_VOLUME ||
      oscPanes.includes(pane.id);
    for (const series of pane.series) {
      visible[series.id] = paneOn && series.declared !== false;
    }
  }
  // Event and state series belong to no value pane — they are markers, shaded
  // spans and tooltip chips. They start on when declared: a bars-since-cross
  // or a GMMA condition flag is the most explanatory thing on the chart, not
  // clutter to be opted into.
  for (const series of seriesList) {
    if (series.id in visible) continue;
    visible[series.id] =
      isAnnotation(series) && series.declared !== false;
  }
  return visible;
}

// ── formatting ─────────────────────────────────────────────────────────────

const inr = new Intl.NumberFormat('en-IN', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export function formatVolume(v) {
  if (v == null) return '—';
  if (v >= 1_00_00_000) return `${(v / 1_00_00_000).toFixed(2)}Cr`;
  if (v >= 1_00_000) return `${(v / 1_00_000).toFixed(2)}L`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K`;
  return String(v);
}

export function formatPrice(v) {
  if (v == null) return '—';
  return inr.format(v);
}

export function formatValue(series, value) {
  if (value == null) return '—';
  if (series.kind === 'bool') return value ? 'yes' : 'no';
  if (series.kind === 'state') return String(value);
  if (series.kind === 'event') {
    if (typeof value !== 'number') return 'inactive';
    return value === 0 ? 'today' : `${value} bar${value === 1 ? '' : 's'} ago`;
  }
  if (series.pane === PANE_VOLUME) return formatVolume(value);
  if (series.pane === PANE_PRICE) return formatPrice(value);
  if (typeof value !== 'number') return String(value);
  return Math.abs(value) >= 100 ? value.toFixed(1) : value.toFixed(2);
}

export function formatAxisTick(kind) {
  if (kind === PANE_VOLUME) return formatVolume;
  if (kind === PANE_PRICE) return (v) => (v == null ? '' : Math.round(v).toLocaleString('en-IN'));
  return (v) => (v == null ? '' : Number(v).toFixed(Math.abs(v) >= 10 ? 0 : 1));
}

/** `2025-06-12` -> `12 Jun` for axis ticks. */
export function shortDate(iso) {
  if (!iso) return '';
  const [, m, d] = iso.split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${Number(d)} ${months[Number(m) - 1] || ''}`;
}
